import React, { useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import "../../../assets/styles/invoiceprint.css";
import { PRINT_TEXT } from "@utils/constants/print.constants";
import logoWithText from "@assets/images/Logo.jpg";
import useGetOneTripInvoice from "@hooks/trip/useGetOneTripInvoice";
import { formatDBDate } from "@utils/general/format-db-date";
import { thousandSeperator } from "@utils/general/digit-separators";

const TripInvoicePrintPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { tripInvoiceData, tripInvoiceLoading, tripInvoiceRefetch } =
    useGetOneTripInvoice(id!);
  const retrieveValues = {
    invoice_date: tripInvoiceData?.invoice_date,
    SSIC: tripInvoiceData?.trip_sale_invoice_code,
    SSOC: tripInvoiceData?.trip_sale_code,
    grq_sale_order_no: tripInvoiceData?.reference_id,
    sales_person_name: tripInvoiceData?.sales_person_name,
    delivery_date: tripInvoiceData?.delivery_date,
    customer_name: tripInvoiceData?.customer_name,
    customer_phone: tripInvoiceData?.cus_phone,
    address: tripInvoiceData?.address,
    contact_name: tripInvoiceData?.contact_name,
    customer_address: tripInvoiceData?.customer_address,
    sub_total: tripInvoiceData?.sub_total,
    discount_amount: tripInvoiceData?.discount_amount,
    tax_amount: tripInvoiceData?.tax_amount,
    other_charges: tripInvoiceData?.other_charges,
    grand_total_amount: tripInvoiceData?.grand_total_amount,
    payment_type_name: tripInvoiceData?.payment_type_name,
    item_detail: tripInvoiceData?.products,
  };
  const chunkArray = (arr: any[], size: number) => {
    const chunks = [];
    for (let i = 0; i < arr.length; i += size) {
      chunks.push(arr.slice(i, i + size));
    }
    return chunks;
  };

  const chunkedItems = chunkArray(retrieveValues?.item_detail || [], 3);
  useEffect(() => {
    const handleAfterPrint = () => navigate(-1);
    window.addEventListener("afterprint", handleAfterPrint);
    return () => window.removeEventListener("afterprint", handleAfterPrint);
  }, [navigate]);

  useEffect(() => {
    if (!tripInvoiceLoading && tripInvoiceData) {
      // Wait a bit for UI to finish rendering
      const timer = setTimeout(() => {
        window.print();
      }, 300);

      return () => clearTimeout(timer);
    }
  }, [tripInvoiceLoading, tripInvoiceData]);

  return (
    <div className="print-page">
      <div className="invoice-container relative min-h-[calc(100vh-2rem)] pb-20">
        {/* Header Section */}
        <div className="header mb-4 text-left">
          {/* <h1 className="text-xl font-bold mb-2 text-[18px]">Mg Kaung</h1> */}
          <img
            width={60}
            height={50}
            className="mb-3"
            alt="Logo"
            src={logoWithText}
          />

          <p className="text-sm mb-2">{PRINT_TEXT.invoice_voucher_text_one}</p>
        </div>

        <div className="border-b border-black mb-2 "></div>

        {/* Invoice Details */}
        <div className="invoice-details">
          <table className="details-table">
            <tbody>
              <tr>
                <td className="bill-to">
                  <p className="text-nowrap">
                    <strong>Bill To:</strong> {retrieveValues?.customer_name}
                  </p>
                  <p>{retrieveValues?.address}</p>
                  <p>Phone: {retrieveValues?.customer_phone}</p>
                  <p>Attention: {retrieveValues?.contact_name}</p>
                  <p>{retrieveValues?.customer_address}</p>
                </td>

                <td className=" invoice-info mt-5">
                  <table>
                    <tbody>
                      <tr>
                        <td className="border-remove"></td>
                        <td className="border-remove text-center">
                          <h1 className="text-[20px]">Invoice</h1>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Items Table */}
        <div className="invoice-body">
          <table className="items-table">
            <thead>
              <tr>
                <td colSpan={4} className="border-remove"></td>
                <td className="label">Invoice No</td>
                <td className="label">
                  {retrieveValues?.SSIC?.slice(0, 13) ?? ""}
                  {/* {retrieveValues?.SSIC ?? ""} */}
                </td>
              </tr>
              <tr>
                <td colSpan={4} className="border-remove"></td>
                <td className="label">Invoice Date</td>
                <td className="label">
                  {retrieveValues?.invoice_date?.split("T")[0] ?? "-"}
                </td>
              </tr>
              <tr>
                <td colSpan={4} className="border-remove"></td>
                <td className="label">Page</td>
                <td className="label">1</td>
              </tr>
              <tr>
                <td colSpan={3} className=" invoice-info-th"></td>
                <td className="label">Sale Person</td>
                <td className="label">Delivery Date</td>
                <td className="label">T.O.Number</td>
              </tr>
              <tr>
                <td colSpan={3} className=" invoice-info-th"></td>

                <td className="text-center border-bottom-remove">
                  {retrieveValues?.sales_person_name}
                </td>
                <td className="text-center border-bottom-remove">
                  {retrieveValues?.delivery_date?.split("T")[0] ?? "-"}
                </td>
                <td className="text-center border-bottom-remove w-full ">
                  {retrieveValues?.SSOC?.slice(0, 13) ?? ""}
                </td>
              </tr>

              <tr className="table-header">
                <th className="text-left">ITEM NO</th>
                <th className="text-left">DESCRIPTION</th>
                <th className="text-center">QTY</th>
                <th className="text-right">PRICE</th>
                <th className="text-center">UNIT</th>
                <th className="text-right">AMOUNT</th>
              </tr>
            </thead>
            <tbody>
              {chunkedItems.map((group, groupIndex) => (
                <React.Fragment key={groupIndex}>
                  {group.map((item: any, index: number) => (
                    <tr key={index}>
                      <td>{index + 1}</td>
                      <td>{item?.product_name}</td>
                      <td className="text-center">{item?.qty}</td>
                      <td className="text-right">{thousandSeperator(item?.sales_price || 0)}</td>
                      <td className="text-center">{item?.unit_name}</td>
                      <td className="text-right">{thousandSeperator(item?.amount || 0)}</td>
                    </tr>
                  ))}
                  {groupIndex < chunkedItems.length - 1 && (
                    <tr className="page-break-row">
                      <td colSpan={6} className="page-break"></td>
                    </tr>
                  )}
                </React.Fragment>
              ))}

              {/* Summary section, only show after last chunk */}
              <tr className="total-row">
                <td colSpan={2} className="border-remove text-left">
                  {retrieveValues?.payment_type_name}
                </td>
                <td colSpan={3} className="border-remove text-right">
                  {PRINT_TEXT.total_amount}
                </td>
                <td className="text-right">{thousandSeperator(retrieveValues?.sub_total || 0)}</td>
              </tr>
              <tr className="grand-total">
                <td colSpan={5} className="border-remove text-right">
                  {PRINT_TEXT.transport_charge}
                </td>
                <td className="text-right">
                  {thousandSeperator(retrieveValues?.discount_amount || 0)}
                </td>
              </tr>
              <tr className="total-row">
                <td colSpan={5} className="border-remove text-right">
                  {PRINT_TEXT.sale_tax}
                </td>
                <td className="text-right">{thousandSeperator(retrieveValues?.tax_amount || 0)}</td>
              </tr>
              <tr className="grand-total">
                <td colSpan={5} className="border-remove text-right">
                  {PRINT_TEXT.balance}
                </td>
                <td className="text-right">
                  {thousandSeperator(retrieveValues?.grand_total_amount || 0)}
                </td>
              </tr>
              <tr className="grand-total">
                <td colSpan={3} className="border-remove text-left">
                  {PRINT_TEXT.slae_invoice_note}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        {/* Footer */}

        {/* <div className="end-line border-b border-black w-full"></div> */}
      </div>
      <div className="footer right-0 bottom-0 absolute">
        <div className="signature mt-5">
          <p>{PRINT_TEXT.authorized}</p>
        </div>
      </div>
    </div>
  );
};

export default TripInvoicePrintPage;
